import styled from 'styled-components';
import dayjs from 'dayjs';

import { FixedTicket, TicketState, UnfixedTicket } from 'types/api';
import ProductStatusBadge from './ProductStatusBadge';

const BookingInfoBox = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;

  width: 100%;
  padding: 12px 16px;
  border-radius: 12px;

  background-color: #f7f7f8;

  cursor: pointer;
`;

const InfoContentBox = styled.div<{ $isCanceled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;

  > p {
    width: 100%;
    display: -webkit-box;

    font-weight: 400;
    font-size: 13px;
    line-height: 18px;

    color: ${({ $isCanceled }) => ($isCanceled ? '#C2C4C8' : '#70737C')};

    overflow: hidden;
    text-overflow: ellipsis;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  > div {
    display: flex;
    align-items: center;
    gap: 8px;

    flex-wrap: wrap;
  }
`;

const BadgeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;

  > div[data-type='tag'] {
    padding: 0px 6px;
    border-radius: 6px;
    background: #dce2e9;

    font-weight: 600;
    font-size: 11px;
    line-height: 24px;
    letter-spacing: -0.2px;
    text-align: center;
    color: #414b56;
  }
`;

const TimeText = styled.p<{ $isCanceled: boolean }>`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;

  color: ${({ $isCanceled }) => ($isCanceled ? '#C2C4C8' : '#1B1C1E')};
`;

const ThumbnailBox = styled.div`
  min-width: 36px;
  min-height: 36px;
  max-width: 36px;
  max-height: 36px;

  border-radius: 6px;

  overflow: hidden;

  background-color: #fff;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/* 조율형 */
const UnfixedBookingCard = ({ item }: { item: UnfixedTicket }) => {
  const startTime = item.scheduleTerm.startedAt;
  const endTime = item.scheduleTerm.endedAt;

  const isSameDay =
    dayjs(startTime).format('YYYY-MM-DD') ===
    dayjs(endTime).format('YYYY-MM-DD');

  const endDay = dayjs(new Date(endTime));
  const startDay = dayjs(new Date(startTime));

  const dayDiff = endDay.diff(startDay, 'day');

  const isCanceled = item.status === TicketState.CANCELED;

  const isExactlySameDay =
    endTime ===
    startTime; /* 대부분 조율형인경우. 예약확정시 duration을 0으로 보내기때문에 */

  return (
    <>
      <InfoContentBox $isCanceled={isCanceled}>
        <p>{item.bookingItem?.booking.info.product?.title}</p>

        <div>
          <BadgeBox>
            <ProductStatusBadge item={item} />
            {/* TODO: 조율형 잘 나오는지 확인 필요 */}
            {!isSameDay && dayDiff + 1 > 1 && (
              <div data-type="tag">{dayDiff + 1}일간</div>
            )}
          </BadgeBox>

          <TimeText $isCanceled={isCanceled}>
            {dayjs(startTime).format('H:mm')} ~{' '}
            {isExactlySameDay
              ? ''
              : isSameDay
              ? endDay.format('H:mm')
              : `${dayjs(endTime).format('D일(ddd)')} ${dayjs(endTime).format(
                  'H:mm',
                )}`}
          </TimeText>
        </div>
      </InfoContentBox>

      <ThumbnailBox>
        <img
          src={item.bookingItem?.booking.info.product?.content?.thumbnail}
          alt="thumbnail"
        />
      </ThumbnailBox>
    </>
  );
};

/* 일정 지정형 */
const FixedBookingCard = ({ item }: { item: FixedTicket }) => {
  const stock = item.counts.quota - item.counts.sale;

  const startTime = item.term.startedAt;
  const endTime = item.term.endedAt;

  const isExactlySameDay =
    endTime ===
    startTime; /* 대부분 조율형인경우. 예약확정시 duration을 0으로 보내기때문에 */

  // 시작 날짜와 종료 날짜가 같은지 확인
  const isSameDay =
    dayjs(startTime).format('YYYY-MM-DD') ===
    dayjs(endTime).format('YYYY-MM-DD');

  const endDay = dayjs(new Date(endTime));
  const startDay = dayjs(new Date(startTime));

  const dayDiff = endDay.diff(startDay, 'day');

  const isCanceled = item.status === TicketState.CANCELED;

  return (
    <>
      <InfoContentBox $isCanceled={isCanceled}>
        <p>{item.product.title}</p>

        <div>
          <BadgeBox>
            <ProductStatusBadge item={item} />

            {!isCanceled && !isSameDay && dayDiff + 1 > 1 && (
              <div data-type="tag">{dayDiff + 1}일간</div>
            )}
          </BadgeBox>

          <TimeText $isCanceled={isCanceled}>
            {dayjs(startTime).format('H:mm')} ~
            {isExactlySameDay
              ? ''
              : isSameDay
              ? endDay.format('H:mm')
              : `${endDay.format(
                  `${dayDiff > 4 ? 'M월 D일(ddd)' : 'D일(ddd)'}`,
                )} ${endDay.format('H:mm')}`}
          </TimeText>
        </div>
      </InfoContentBox>

      <ThumbnailBox>
        <img
          src={item.product.headerContents[0].content.thumbnail}
          alt="thumbnail"
        />
      </ThumbnailBox>
    </>
  );
};

interface Props {
  item: UnfixedTicket | FixedTicket;
  selectItemHandler: (item: UnfixedTicket | FixedTicket) => void;
}

function BookingCardV2({ item, selectItemHandler }: Props) {
  const isUnfixedTicket = 'bookingItem' in item ? true : false;

  return (
    <BookingInfoBox onClick={() => selectItemHandler(item)}>
      {isUnfixedTicket ? (
        <UnfixedBookingCard item={item as UnfixedTicket} />
      ) : (
        <FixedBookingCard item={item as FixedTicket} />
      )}
    </BookingInfoBox>
  );
}

export default BookingCardV2;
